import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { queryEncode } from 'cio/helpers/query';
import serializeFilters from 'cio/utils/recipients-filter/serialize';
import CampaignNavStepController from 'cio/controllers/campaign-nav-step';
import serializePeopleFilters from 'cio/utils/conditions/serialize/filter';
import CampaignModel, { CampaignUpdateTypes } from 'cio/models/campaign';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';

export default class IndexController extends CampaignNavStepController {
  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;
  @service environment!: EnvironmentService;
  @service auth!: AuthService;

  declare model: CampaignModel;

  supportedConditionTypes = ['segments', 'segments_negatable', 'attribute', 'device_attribute', 'message'];

  recipientsFilter: SerializedLegacyFilter[] | null = null;
  sendToUnsubscribed = null;
  useMessageLimits = null;

  get conditionTypes() {
    let conditionTypes = this.supportedConditionTypes;
    if (!this.environment.enabledAction('push')) {
      conditionTypes = conditionTypes.filter((t) => t !== 'device_attribute');
    }
    return conditionTypes;
  }

  @computed('recipientsFilter')
  get encodedRecipientsFilter() {
    return queryEncode(serializePeopleFilters(this.recipientsFilter ?? [], 'and', true));
  }

  @computed('recipientsFilter', 'model.recipientsFilter')
  get _areRecipientSettingsDirty() {
    let modelRecipientsFilter = serializeFilters(this.model.recipientsFilter);
    let recipientsFilter = serializeFilters(this.recipientsFilter);

    return JSON.stringify(recipientsFilter) !== JSON.stringify(modelRecipientsFilter);
  }

  @computed('sendToUnsubscribed', 'model.sendToUnsubscribed')
  get _isSendToUnsubscribedDirty() {
    return this.sendToUnsubscribed !== this.model.sendToUnsubscribed;
  }

  @computed('useMessageLimits', 'model.useMessageLimits')
  get _isUseMessageLimitsDirty() {
    return this.useMessageLimits !== this.model.useMessageLimits;
  }

  @computed('_areRecipientSettingsDirty', '_isSendToUnsubscribedDirty', '_isUseMessageLimitsDirty')
  get isDirty() {
    return this._areRecipientSettingsDirty || this._isSendToUnsubscribedDirty || this._isUseMessageLimitsDirty;
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  prepareForEdit(campaign: CampaignModel) {
    this.setProperties({
      recipientsFilter: campaign.recipientsFilter,
      sendToUnsubscribed: campaign.sendToUnsubscribed,
      useMessageLimits: campaign.useMessageLimits,
    });
  }

  saveCampaignTask = task(async () => {
    let { model } = this;

    try {
      // Potentially calls save on the broadcast twice, because the recipients
      // page shows both recipient settings, and send to unsubscribe settings.
      // These two have different updateTypes.
      if (this._areRecipientSettingsDirty) {
        model.setProperties({
          recipientsFilter: this.recipientsFilter,
          updateType: CampaignUpdateTypes.Recipients,
        });

        await model.save();
      }

      if (this._isSendToUnsubscribedDirty || this._isUseMessageLimitsDirty) {
        model.setProperties({
          sendToUnsubscribed: this.sendToUnsubscribed,
          useMessageLimits: this.useMessageLimits,
          updateType: CampaignUpdateTypes.SendToUnsubscribed,
        });

        await model.save();
      }

      this.prepareForEdit(model);
    } catch (err) {
      model.rollbackAttributes();
    }
  });

  resetTask = task(async () => {
    await this.prepareForEdit(this.model);
  });

  @action
  updateRecipientsFilter(conditions: SerializedLegacyFilter[]) {
    this.set('recipientsFilter', conditions);
  }

  @action
  trackSegmentCreationStartedFrom(source: string) {
    this.analytics.track('segment', 'creation_started', { source });
  }
}
